<template>
  <v-container fluid>
    <v-breadcrumbs :items="breadItems" class="breadcrumb mx-8">
    </v-breadcrumbs>

    <v-row dense class="d-flex mx-8">
      <v-col cols="9" sm="9" md="9" lg="9">
        <v-expansion-panels multiple focusable class="" v-model="panels">
          <v-expansion-panel id="intro">
            <v-expansion-panel-header class="font-weight-bold">
              <span class="info--text">1. Introduction</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <br>
              <h4 class="">1.1 Alternative Splicing and Cancer</h4>
              <br>
              <p>Pre-mRNA splicing is a fundamental process in mammalian gene expression, with nearly 95% of human genes
                undergoing alternative splicing (AS) to generate protein diversity.
                Over the past decade, there have been extensive evidences showing that aberrations in mRNA splicing
                contribute to the initiation and progression of many cancers through diverse cellular processes.
                The recent large-scale genomic analysis has revealed the mutational landscape of splicing-related genes in
                human cancers, which provides genetic evidence directly linking RNA splicing dysregulation to cancers.
              </p>
              <p>
                Unfortunately, there is still a lack of comprehensive resource of AS in human cancers, which serves as not
                merely a collection of computationally putative AS events for correlation-based analysis,
                but more importantly, a knowledgebase of key functional carcinogenic AS events together with its upstream
                splicing regulators, downstream oncogenic effects and possible therapeutic strategies.
              </p>
              <br>
              <h4 class="">1.2 Overview of ASCancer Atlas</h4>
              <br>
              <p>Here, we present <b>ASCancer Atlas</b> (<a
                  href="https://ngdc.cncb.ac.cn/ascancer">https://ngdc.cncb.ac.cn/ascancer</a>), a comprehensive
                knowledgebase of alternative splicing in human cancers.
                In the current release, ASCancer Atlas houses about 2 million <b>Computationally Putative Splicing Events
                  (CPSE)</b>, covering 33 TCGA cancer types and 31 GTEx normal tissues.
                More importantly and distinctively, ASCancer Atlas also builds the first repository of experimentally
                validated <b>Cancer Associated Splicing Events (CASE)</b> with established
                oncogenic roles summarized from extensive functional studies. A total of 2,141 high-confidence CASE with
                detailed functional axis were manually curated from 610 publications,
                involving 29 cancer primary sites and 60 human cancer subtypes. In addition, <b>an interactive splicing
                  visualization tool</b> and <b>a multiple-dimensional online splicing analysis toolkit</b>
                were equipped to further explore the splicing events.
              </p>
              <p>Collectively, ASCancer Atlas provides the first knowledgebase of carcinogenic AS in human cancers, which
                can
                help users to investigate the full spectrum of splicing dysregulation in cancers and
                will become a value-added resource when more and more splicing variants are
                identified as targets for cancer treatment.
              </p>
              <v-img src="@/assets/img/help/fig1.png" max-width="60%" contain class="mx-auto"></v-img>
              <br>
              <p class="text-center"><b>Figure 1.</b> Overview of ASCancer Atlas.</p>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel id="case">
            <v-expansion-panel-header class="font-weight-bold">
              <span class="info--text">2. Cancer Associated Splicing Events (CASE)</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <br>
              <h4 class="">2.1 Data Curation</h4>
              <br>
              <p>To provide high-confidence <b>Cancer Associated Splicing Events (CASE)</b>, ASCancer Atlas set up a
                standardized and stringent curation process, mainly involving literature searching and filtering,
                as well as information retrieval and annotations. First, we screened abundant literatures in PubMed using
                a list of keywords search, such as ‘alternative splicing’, ‘splicing variant’ and ‘tumor’ etc.,
                and picked out the CASE. Second, we manually curated the complete functional axis of mis-splicing events,
                including upstream splicing regulations, downstream oncogenic effects and potential therapeutically
                interventions (<b>Figure 2</b>).
                In addition, we also annotated the splicing events based on AS types (<b>Figure 3</b>).</p>
              <br>
              <v-img src="@/assets/img/help/fig2.png" max-width="60%" contain class="mx-auto">
              </v-img>
              <br>
              <p class="text-center ml-16"><b>Figure 2.</b> The curation process of CASE.</p>
              <br>
              <v-img src="@/assets/img/help/fig3.png" max-width="60%" contain class="mx-auto">
              </v-img>
              <br>
              <p class="text-center"><b>Figure 3.</b> The coordinates representation of different AS types.</p>

              <h4 class="">2.2 Curation Models of CASE</h4>
              <br>
              <v-simple-table dense fixed-header height="400px">
                <template v-slot:default>
                  <thead class="font-weight-bold">
                    <tr>
                      <th class="text-left" style="width:5%">Field ID</th>
                      <th class="text-left" style="width:15%">Field Name</th>
                      <th class="text-left" style="width:40%">Filed Description</th>
                      <th class="text-left" style="width:45%">Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>event_id</td>
                      <td>Event ID</td>
                      <td>The unique identifier of alternative splicing event. </td>
                      <td>EIF4H_chr7_+_ES_73604152:73604248:73604577:73604636:73609071:73609208</td>
                    </tr>
                    <tr>
                      <td>as_model_id</td>
                      <td>AS Model ID</td>
                      <td>The unique identifier of alternative splicing model. </td>
                      <td>EIF4H_chr7_+_ES_73604248:73604577:73604636:73609071</td>
                    </tr>
                    <tr>
                      <td>cancer_name</td>
                      <td>Cancer Name</td>
                      <td>Cancer type or cancer subtype.</td>
                      <td>Lung Cancer</td>
                    </tr>
                    <tr>
                      <td>gene_name</td>
                      <td>Gene Name</td>
                      <td>Gene name in HGNC.</td>
                      <td>EIF4H</td>
                    </tr>
                    <tr>
                      <td>hgnc_id</td>
                      <td>HGNC ID</td>
                      <td>Gene ID in HGNC.</td>
                      <td>12741</td>
                    </tr>
                    <tr>
                      <td>ensembl_id</td>
                      <td>Ensembl Gene ID</td>
                      <td>Gene ID in Ensembl GRCh37 Release 105.</td>
                      <td>ENSG00000106682</td>
                    </tr>
                    <tr>
                      <td>chr</td>
                      <td>Chr</td>
                      <td>Chromosome No.</td>
                      <td>chr7</td>
                    </tr>
                    <tr>
                      <td>strand</td>
                      <td>Strand</td>
                      <td>Forward strand (+) and reverse strand (-).</td>
                      <td>+</td>
                    </tr>
                    <tr>
                      <td>canonical_transcript_id</td>
                      <td>Canonical Transcript</td>
                      <td>The canonical transcript of gene in Ensembl GRCh37 Release 105.</td>
                      <td>EIF4H-001</td>
                    </tr>
                    <tr>
                      <td>ct_ensembl_id</td>
                      <td>Canonical Transcript Ensembl ID</td>
                      <td>The canonical transcript ID of gene in Ensembl GRCh37 Release 105.</td>
                      <td>ENST00000265753</td>
                    </tr>
                    <tr>
                      <td>ct_refSeq_id</td>
                      <td>Canonical Transcript RefSeq ID</td>
                      <td>The canonical transcript ID of gene in RefSeq.</td>
                      <td>NM_022170</td>
                    </tr>
                    <tr>
                      <td>canonical_transcript_description</td>
                      <td>Canonical Transcript RefSeq Description</td>
                      <td>The canonical transcript description of gene in RefSeq.</td>
                      <td>This variant (1) encodes the longer isoform (1).</td>
                    </tr>
                    <tr>
                      <td>splice_variant_id</td>
                      <td>Splicing Variant</td>
                      <td>The splicing variant of gene in Ensembl GRCh37 Release 105.</td>
                      <td>EIF4H-002</td>
                    </tr>
                    <tr>
                      <td>sv_ensembl_id</td>
                      <td>Splicing Variant Ensembl ID</td>
                      <td>The splicing variant ID of gene in Ensembl GRCh37 Release 105.</td>
                      <td>ENST00000353999</td>
                    </tr>
                    <tr>
                      <td>sv_refSeq_id</td>
                      <td>Splicing Variant RefSeq ID</td>
                      <td>The splicing variant ID of gene in RefSeq.</td>
                      <td>NM_031992</td>
                    </tr>
                    <tr>
                      <td>splice_variant_description</td>
                      <td>Splicing Variant RefSeq Description</td>
                      <td>The splicing variant description of gene in RefSeq.</td>
                      <td>This variant (2) lacks an in-frame exon in the coding region, compared to variant 1. This
                        variant encodes isoform 2 which is shorter, compared to isoform 1.</td>
                    </tr>
                    <tr>
                      <td>as_type</td>
                      <td>Alternative Splicing Type</td>
                      <td>ES, A3SS, A5SS, MEX, IR, Complex.</td>
                      <td>ES</td>
                    </tr>
                    <tr>
                      <td>related_exons</td>
                      <td>Event Related Exons</td>
                      <td>The Ensembl exon IDs related to splicing event.</td>
                      <td>ENSE00003615791, ENSE00003491152, ENSE00000693358</td>
                    </tr>
                    <tr>
                      <td>exon_ensembl_id</td>
                      <td>Altered Exons</td>
                      <td>The Altered Ensembl exon IDs.</td>
                      <td>ENSE00003491152</td>
                    </tr>
                    <tr>
                      <td>event_coordinates</td>
                      <td>Event Coordinates</td>
                      <td>The event coordinates of alternative splicing.</td>
                      <td>73604152:73604248:73604577:73604636:73609071:73609208</td>
                    </tr>
                    <tr>
                      <td>alt_coordinates</td>
                      <td>Altered Coordinates</td>
                      <td>The altered coordinates of alternative splicing.</td>
                      <td>73604577:73604636</td>
                    </tr>
                    <tr>
                      <td>splice_in_isoforms</td>
                      <td>Splice In Isoform</td>
                      <td>Transcipts with splice junctions support exon splice in.</td>
                      <td>ENST00000265753</td>
                    </tr>
                    <tr>
                      <td>splice_out_isoforms</td>
                      <td>Splice Out Isoform</td>
                      <td>Transcipts with splice junctions support exon splice out.</td>
                      <td>ENST00000353999</td>
                    </tr>
                    <tr>
                      <td>orf_annotation</td>
                      <td>ORF Annotations</td>
                      <td>The ORF annotaions of splicing event (in-frame or frame-shift).</td>
                      <td>in-frame</td>
                    </tr>
                    <tr>
                      <td>primer_f</td>
                      <td>Forward Primer</td>
                      <td>The forward primer used in PCR.</td>
                      <td>TCACTTCGTGTGGACATTGC</td>
                    </tr>
                    <tr>
                      <td>primer_r</td>
                      <td>Reverse Primer</td>
                      <td>The reverse primer used in PCR.</td>
                      <td>CCTGCCCCCTAAGAAGTCAT</td>
                    </tr>
                    <tr>
                      <td>cell_line</td>
                      <td>Cell Line</td>
                      <td>The cell lines used in the experiment.</td>
                      <td>BEAS2B, PC9</td>
                    </tr>
                    <tr>
                      <td>primary_site</td>
                      <td>Primary Site</td>
                      <td>The cancer primary site of splicing event.</td>
                      <td>Lung</td>
                    </tr>
                    <tr>
                      <td>samples</td>
                      <td>Samples</td>
                      <td>The samples used in the experiment.</td>
                      <td>314 LUAD patients from a Chinese patient cohort and The Cancer Genome Atlas (TCGA)</td>
                    </tr>
                    <tr>
                      <td>methods</td>
                      <td>Experimental Methods</td>
                      <td>The experimental methods used to validate the splicing event.</td>
                      <td>RT-PCR</td>
                    </tr>
                    <tr>
                      <td>external_intervention</td>
                      <td>External Intervention</td>
                      <td>External Intervention.</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>regulatory_mechanism</td>
                      <td>Regulatory Mechanism</td>
                      <td>The regulatory mechanism of splicing event.</td>
                      <td>splicing factor, mutation</td>
                    </tr>
                    <tr>
                      <td>regulatory_gene</td>
                      <td>Splicing Regulator</td>
                      <td>The splicing regulators of event.</td>
                      <td>RBM10</td>
                    </tr>
                    <tr>
                      <td>biological_function</td>
                      <td>Biological Function</td>
                      <td>The splicing effects of event.</td>
                      <td>cell proliferation</td>
                    </tr>
                    <tr>
                      <td>functional_description</td>
                      <td>Functional Description</td>
                      <td>The functional description of splicing event. </td>
                      <td>RBM10 suppresses LUAD progression largely by regulating alternative splicing of
                        EIF4H exon 5. Loss of RBM10 in LUAD enhances the expression of EIF4H-L in LUAD. EIF4H-L,
                        but not EIF4H-S, is critical for LUAD cell proliferation, survival and tumourigenesis. The
                        inclusion of EIF4H exon 5 could serve as a valuable therapeutic target in lung cancer.</td>
                    </tr>
                    <tr>
                      <td>year</td>
                      <td>Year</td>
                      <td>Published year.</td>
                      <td>2020</td>
                    </tr>
                    <tr>
                      <td>pubmed_id</td>
                      <td>PubMed ID</td>
                      <td>PubMed ID.</td>
                      <td>33130397</td>
                    </tr>
                    <tr>
                      <td>journal</td>
                      <td>Journal</td>
                      <td>Jounal Name.</td>
                      <td>EBioMedicine</td>
                    </tr>
                    <tr>
                      <td>title</td>
                      <td>Title</td>
                      <td>Literature title.</td>
                      <td>RNA binding motif protein 10 suppresses lung cancer progression by controlling alternative
                        splicing of eukaryotic translation initiation factor 4H</td>
                    </tr>
                    <tr></tr>
                  </tbody>
                </template>
              </v-simple-table>
              <br>
              <p class="text-center"><b>Table 1.</b> Format of Single CASE Entry.</p>

              <h4 class="">2.3 User Interfaces of CASE</h4>
              <br>
              <p>CASE mainly involves the following user interfaces, please refer to the
                <span class="font-weight-bold info--text">User Guide</span>
                <v-btn class="primary mx-2" fab x-small icon style="cursor:auto">
                  <v-icon color="white">mdi-book-cog</v-icon>
                </v-btn>
                (if any) at the bottom-right corner of the corrsponding pages:
              </p>
              <ol>
                <li class="mt-2">
                  <v-btn small color="rgb(245, 177, 73)" to="/tools/regnet" outlined>CASE Regulation</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="rgb(114, 187, 232)" to="/browse/knowledge" outlined>CASE Browser</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="rgb(114, 187, 232)"
                    to="/browse/knowledge/details?eventid=EZH2_chr7_-_ES_148515129%3A148515209%3A148516070%3A148516151%3A148516688%3A148516779&cancername=Myelodysplastic%20Syndrome&pubmedid=25965569"
                    outlined>CASE Details</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="rgb(165, 212, 172)" to="/biofunc?functional=-&page=1&size=15" outlined>CASE
                    Effect</v-btn>
                </li>
              </ol>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel id="cpse">
            <v-expansion-panel-header class="font-weight-bold">
              <span class="info--text">3. Computationally Putative Splicing Events (CPSE)</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <br>
              <h4 class="">3.1 Data Collection and Processing</h4>
              <br>
              <p>
                ASCancer Atlas houses about 2 million <b>Computationally Putative Splicing Events (CPSE)</b>,
                covering <a @click="$router.push({ path: '/projects/tcga' })">33 TCGA cancer types</a> and
                <a @click="$router.push({ path: '/projects/gtex' })">31 GTEx tissue types</a>.
              </p>
              <p>
                The CPSE were mined from the pan-cancer atlas project of alternative splicing analysis
                of 8,705 tumor patients across 33 cancer types and 31 normal tissues
                [<a href="https://pubmed.ncbi.nlm.nih.gov/30078747/" target="_blank">PMID=30078747</a>].
                The detailed workflow is shown in Figure 4.
              </p>
              <br>
              <v-img src="@/assets/img/help/fig4.png" max-width="40%" contain class="mx-auto">
              </v-img>
              <br>
              <p class="text-center"><b>Figure 4.</b> The workflow of data collection and processing for the CPSE.
                Abbreviations: DASE (Differential Alternative Splicing Events).</p>
              <br>
              <h4 class="">3.2 Data Models of CPSE</h4>
              <br>
              <v-simple-table dense fixed-header height="400px">
                <template v-slot:default>
                  <thead class="font-weight-bold">
                    <tr>
                      <th class="text-left" style="width:5%">Field ID</th>
                      <th class="text-left" style="width:15%">Field Name</th>
                      <th class="text-left" style="width:30%">Filed Description</th>
                      <th class="text-left" style="width:50%">Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>event_id</td>
                      <td>Event ID</td>
                      <td>The unique identifier of alternative splicing event. </td>
                      <td>NUMB_chr14_-_ES_73743917:73744001:73745989:73746090:73749067:73749213</td>
                    </tr>
                    <tr>
                      <td>as_model_id</td>
                      <td>AS Model ID</td>
                      <td>The unique identifier of alternative splicing model. </td>
                      <td>NUMB_chr14_-_ES_73744001:73745989:73746090:73749067</td>
                    </tr>
                    <tr>
                      <td>gene_name</td>
                      <td>Gene Name</td>
                      <td>Gene name in HGNC.</td>
                      <td>NUMB</td>
                    </tr>
                    <tr>
                      <td>ensemble_id</td>
                      <td>Ensembl Gene ID</td>
                      <td>Gene ID in Ensembl GRCh37 Release 105.</td>
                      <td>ENSG00000133961</td>
                    </tr>
                    <tr>
                      <td>chr</td>
                      <td>Chr</td>
                      <td>Chromosome No.</td>
                      <td>chr14</td>
                    </tr>
                    <tr>
                      <td>strand</td>
                      <td>Strand</td>
                      <td>Forward strand (+) and reverse strand (-).</td>
                      <td>-</td>
                    </tr>
                    <tr>
                      <td>cancer_name</td>
                      <td>Cancer Name</td>
                      <td>Cancer type.</td>
                      <td>Lung Adenocarcinoma</td>
                    </tr>
                    <tr>
                      <td>primary_site</td>
                      <td>Primary Site</td>
                      <td>The cancer primary site of splicing event.</td>
                      <td>Lung</td>
                    </tr>
                    <tr>
                      <td>as_type</td>
                      <td>Alternative Splicing Type</td>
                      <td>ES, A3SS, A5SS, MEX, IR.</td>
                      <td>ES</td>
                    </tr>
                    <tr>
                      <td>event_coordinates</td>
                      <td>Event Coordinates</td>
                      <td>The event coordinates of alternative splicing.</td>
                      <td>73743917:73744001:73745989:73746090:73749067:73749213</td>
                    </tr>
                    <tr>
                      <td>alt_coordinates</td>
                      <td>Altered Coordinates</td>
                      <td>The altered coordinates of alternative splicing.</td>
                      <td>73745989:73746090</td>
                    </tr>
                    <tr>
                      <td>orf</td>
                      <td>ORF Annotations</td>
                      <td>The ORF annotaions of splicing event (in-frame / frame-shift).</td>
                      <td>in-frame</td>
                    </tr>
                    <tr>
                      <td>source</td>
                      <td>Data Source</td>
                      <td>TCGA / GTEx.</td>
                      <td>TCGA</td>
                    </tr>
                    <tr>
                      <td>project_id</td>
                      <td>Project ID</td>
                      <td>e.g. TCGA-BRCA / GTEx-Breast.</td>
                      <td>TCGA-LUAD</td>
                    </tr>
                    <tr>
                      <td>control</td>
                      <td>Control Group</td>
                      <td>normal.</td>
                      <td>normal</td>
                    </tr>
                    <tr>
                      <td>control_size</td>
                      <td>Control Group Sample Size</td>
                      <td>normal sample size.</td>
                      <td>59</td>
                    </tr>
                    <tr>
                      <td>case</td>
                      <td>Case Group</td>
                      <td>tumor.</td>
                      <td>tumor</td>
                    </tr>
                    <tr>
                      <td>case_size</td>
                      <td>Case Group Sample Size</td>
                      <td>tumor sample size.</td>
                      <td>507</td>
                    </tr>
                    <tr>
                      <td>pct_samples</td>
                      <td>Percent of samples with PSI values</td>
                      <td>Percent of samples with PSI values.</td>
                      <td>0.998</td>
                    </tr>
                    <tr>
                      <td>normal_psi</td>
                      <td>Normal PSI</td>
                      <td>The median PSI value of tumor samples.</td>
                      <td>0.038</td>
                    </tr>
                    <tr>
                      <td>tumor_psi</td>
                      <td>Tumor PSI</td>
                      <td>The median PSI value of normal samples.</td>
                      <td>0.307</td>
                    </tr>
                    <tr>
                      <td>dpsi</td>
                      <td>Delta PSI</td>
                      <td>Tumor PSI - Normal PSI.</td>
                      <td>0.269</td>
                    </tr>
                    <tr>
                      <td>pvalue</td>
                      <td>P Value</td>
                      <td>P value of Wilcoxon rank-sum test.</td>
                      <td>3.53E-35</td>
                    </tr>
                    <tr>
                      <td>fdr</td>
                      <td>FDR Value</td>
                      <td>Benjamini–Hochberg adjust p-value of Wilcoxon rank-sum test.</td>
                      <td>1.21E-31</td>
                    </tr>
                    <tr>
                      <td>is_dase</td>
                      <td>Is DASE ?</td>
                      <td>Differential AS events. Criteria: |dpsi| &gt; 0.1 &amp; fdr &lt; 0.05.</td>
                      <td>yes</td>
                    </tr>
                    <tr>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <br>
              <p class="text-center"><b>Table 2.</b> Format of Single CPSE Entry.</p>
              <br>
              <h4 class="">3.3 User Interfaces of CPSE</h4>
              <br>
              <p>CPSE mainly involves the following user interface:</p>
              <v-btn small color="info" to="/browse/datasets" outlined>CPSE Browser</v-btn>
              <br>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel id="visualize">
            <v-expansion-panel-header class="font-weight-bold">
              <span class="info--text">4. Integration of CASE and CPSE</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <br>
              <p>In order to provide large-cohorts support for CASE, ASCancer Atlas integrates the CASE and CPSE based on
                the AS model ID.
                Thus, users can easily browse, search and visualize all splicing events of single gene in the <b>Gene
                  Summary</b> Page or
                <b>Splicing Visualization</b> Page.
              </p>
              <h4>4.1 Gene Summary</h4>
              <br>
              <p>This page provides the detailed information of gene, including <i>Basic Information</i>, <i>Gene
                  Expression and Isoform Percentage in Pan-Cancer</i>,
                <i>Transcript Information and Structure</i>, <i>Alternative Splicing Models</i>, <i>CASE</i> and
                <i>CPSE</i>.
              </p>
              <br>
              <h4>4.2 Splicing Visualization</h4>
              <br>
              <p>This page provides interactive visualization of all CASE and CPSE of single gene, and shows the PSI
                (Percent-Spliced-In) distribution of splicing events in TCGA pan-cancer.
                Users can intuitively view the splicing changes at the local genomic region, the whole transcript and
                protein structure levels.
                In addition, users can change or custom the splicing events for visualization.</p>
              <br>
              <h4>4.3 User Interfaces</h4>
              <br>
              <p>Please refer to the <span class="font-weight-bold info--text">User Guide</span>
                <v-btn class="primary mx-2" fab x-small icon style="cursor:auto">
                  <v-icon color="white">mdi-book-cog</v-icon>
                </v-btn>
                (if any) at the bottom-right corner of the corrsponding pages:
              </p>
              <ul>
                <li class="mt-2">
                  <v-btn small color="info" to="/search?genename=EZH2" outlined>Gene Summary</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="info"
                    to="/splicevis?genename=EZH2&astype=ES&coordinate=148514969%3A148515209%3A148516070%3A148516151%3A148516688%3A148516779"
                    outlined>Splicing Visualization</v-btn>
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel id="toolkit">
            <v-expansion-panel-header class="font-weight-bold">
              <span class="info--text">5. Splicing Toolkit</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <br>
              <p>ASCancer Atlas has equipped a suit of online splicing analysis tools to further explore the cancer
                splicing in multiple aspects,
                involving potential regulators discovery (<i><b>Splicing Regulatory Network</b></i> and <i><b>Splicing
                    Regulatory Explorer</b></i>),
                clinical relevance (<i><b>Survival Analysis</b></i> and <i><b>Cancer Staging Analysis</b></i>) and
                drug design guidance (<i><b>Splicing-derived Drug Response</b></i> and <i><b>Isoform-specific PPIs
                    Network</b></i>).
              </p>
              <br>
              <h4>5.1 Splicing Regulatory Network</h4>
              <br>
              <p>
                Splicing dysregulation tends to exert physiological functions in the form of coordinated splicing
                networks, not a single event.
                ASCancer Atlas provides so far, the most complete mis-splicing regulatory networks in human cancers,
                which try to link all cancer associated splicing events (from individual studies) together in a systematic
                view for a better understanding of
                the coordinated effects of splicing dysregulation (<b>Figure 5</b>).
                Users can interactively view their interested splicing regulators or the corresponding target genes from a
                systematic and global perspective.
              </p>
              <br>
              <v-img src="@/assets/img/help/fig5.png" max-width="66%" contain class="mx-auto">
              </v-img>
              <br>
              <p class="text-center"><b>Figure 5.</b> Splicing regulatory network.</p>
              <br>
              <h4>5.2 Splicing Regulatory Explorer</h4>
              <br>
              <p>
                Splicing Regulatory Explorer was developed to interactively explore the potential splicing regulators
                based on TCGA multi-omics evidence.
                The current version supports the exploration of potential regulatory mechanism from the levels of genetic
                variants (SNVs/CNVs) and transcriptional expressions.
                Briefly, with selected event and cancer type, the toolkit provides an interactive mutation oncoprint and
                expression heatmap with samples (columns) sorted by the splicing intensity,
                together with statistics from the pre-ranked enrichment test. For each AS event, the top 10 potential
                regulators are presented by default and users can also manually check customized genes.
                In addition, the tool can also help users to view the correlation between gene expression and PSI of AS
                events.
              </p>
              <br>
              <h4>5.3 Survival Analysis</h4>
              <br>
              <p>
                Survival analysis allows users, especially clinicians, to rapidly estimate the prognostic significance of
                a given splicing event in the specified cancer type.
                This may assist them to focus on the candidate splicing events for constructing the prognosis prediction
                model, and ultimately for cancer treatment.
              </p>
              <br>
              <h4>5.4 Cancer Staging Analysis</h4>
              <br>
              <p>
                Cancer Staging Analysis can help users to view the sequential dynamics of a given splicing event at
                different pathological stages of tumor.
                Users can obtain a box-plot and survival plot for a specific splicing event according to patient's
                pathological stages.
              </p>
              <br>
              <h4>5.5 Splicing-derived Drug Response</h4>
              <br>
              <p>
                Splicing-derived Drug Response allows users to explore the impact of alternative splicing on drug-target
                interactions at the isoform level.
                The pipeline of data collection and processing is shown in <b>Figure 6</b>. Users can view the impact of
                different isoforms of the same gene on the drug-gene interactions by entering the gene or drug of
                interest.
                In addition, users can also interactively manipulate the structure of ligand-protein interactions and view
                the result of Multiple Sequence Alignment for each ligand-protein interaction to
                explore how alternative splicing affects drug-gene interactions. This tool would help to develop new
                therapeutic strategies to overcome the splicing-derived drug resistance.
              </p>
              <br>
              <v-img src="@/assets/img/help/fig6.png" max-width="50%" contain class="mx-auto">
              </v-img>
              <br>
              <p class="text-center"><b>Figure 6.</b> The pipeline of data collection and processing for <span
                  class="font-italic">Splicing-derived Drug Response</span>. Abbreviations: MSA (multiple sequence
                alignment), L-R (Ligand-Receptor).</p>
              <br>
              <h4>5.6 Isoform-specific PPIs Network</h4>
              <br>
              <p>
                This tool aims to investigate the functional roles of alternative splicing on protein-protein interactions
                (PPIs) at the isoform level.
                Users can clearly view the missing or retained isoform-specific PPIs network by inputting gene or
                transcript of interest.
                Users can also set the max PPIs number to show. The detailed construction process of isoform-specific PPIs
                network was described in CanIsoNet. [<a href="https://pubmed.ncbi.nlm.nih.gov/30078747/"
                  target="_blank">PubMed=32879328</a>]
              </p>
              <h4>5.7 User Interfaces</h4>
              <br>
              <p>Please refer to the <span class="font-weight-bold info--text">User Guide</span>
                <v-btn class="primary mx-2" fab x-small icon style="cursor:auto">
                  <v-icon color="white">mdi-book-cog</v-icon>
                </v-btn>
                at the bottom-right corner of the corrsponding pages:
              </p>
              <ol>
                <li class="mt-2">
                  <v-btn small color="rgb(245, 177, 73)" to="/tools/regnet" outlined>Splicing Regulatory Network</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="rgb(245, 177, 73)" to="/tools/regexp" outlined>Splicing Regulatory Explorer</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="rgb(114, 187, 232)" to="/tools/survival/asevent" outlined>Survival Analysis</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="rgb(114, 187, 232)" to="/tools/stage/asevent" outlined>Cancer Staging
                    Analysis</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="rgb(165, 212, 172)" to="/tools/splicedrug" outlined>Splicing-derived Drug
                    Response</v-btn>
                </li>
                <li class="mt-2">
                  <v-btn small color="rgb(165, 212, 172)" to="/tools/spliceinter" outlined>Isoform-specific PPIs
                    Network</v-btn>
                </li>
              </ol>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel id="contact">
            <v-expansion-panel-header class="font-weight-bold">
              <span class="info--text">6. Contact US</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <br>
              <v-row dense>
                <v-col cols="6">
                  <b>Email:</b><br> ascancer@big.ac.cn<br><br>
                  <b>Postal Address:</b><br>
                  National Genomics Data Center<br>
                  China National Center for Bioinformation / Beijing Institute of Genomics<br>
                  Chinese Academy of Sciences<br>
                  No.1 Beichen West Road<br>
                  Chaoyang District, Beijing 100101<br>
                  China<br>
                </v-col>
                <v-col cols="6">
                  <v-img src="@/assets/img/address.jpg" max-width="80%"></v-img>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel id="disclaimer">
            <v-expansion-panel-header class="font-weight-bold">
              <span class="info--text">7. Licenses</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <br>
              <v-row dense>
                <v-col cols="12">
                  ASCancer Atlas is free for academic use only.
                  For any commercial use, please contact us for commercial
                  licensing terms.<br>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>

      <v-col cols="3" sm="3" md="3" lg="3" class="side_bar">
        <v-list dense outlined rounded>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="d-flex justify-center font-weight-bold">
                <span style="font-size: 1rem !important;">Table of Contents</span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <div class="d-flex justify-center font-weight-bold">
            <v-chip outlined small color="success" class="mx-2" @click="showAll()">
              Show All
            </v-chip>
            <v-chip outlined small color="error" class="mx-2" @click="showNone()">
              Collapse All
            </v-chip>
          </div>
          <v-divider class="my-2"></v-divider>

          <v-list-item-group v-model="panels" multiple color="info">
            <v-list-item v-for="(item, index) in itemsList" :key="index" @click="goToAnchor(item.url, index)">
              <v-list-item-content>
                <v-list-item-title>
                  {{ index + 1 }}. {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-col>
    </v-row>

    <back-to-top></back-to-top>
  </v-container>
</template>

<script>
import BackToTop from '@/components/main/BackToTop';
import bus from '@/js/eventBus';
export default {
  name: 'Help',
  components: {
    BackToTop
  },
  data() {
    return {
      breadItems: [
        {
          text: 'Home',
          disabled: false,
          href: '/ascancer/home',
        },
        {
          text: 'Help',
          disabled: true,
        }
      ],

      itemsList: [
        { title: 'Introduction', url: '#intro' },
        { title: 'Cancer Associated Splicing Events (CASE)', url: '#case' },
        { title: 'Computationally Putative Splicing Events (CPSE)', url: '#cpse' },
        { title: 'Integration of CASE and CPSE', url: '#visualize' },
        { title: 'Splicing Toolkit', url: '#toolkit' },
        { title: 'Contact US', url: '#contact' },
        { title: 'Licenses', url: '#disclaimer' },
      ],
      panels: [0],
    }
  },
  created() {

  },
  methods: {
    goToAnchor(selector, index) {
      // console.log(this.panels);
      // click to collapse or show
      if (!this.panels.includes(index)) {
        // this.panels.push(index);
        this.panels = [...this.panels, index];
        // this.$set(this.panels, this.panels.length, index);
        // console.log(this.panels);
      } else {
        let i = this.panels.indexOf(index);
        // this.panels.splice(i, 1);
        let panels = [...this.panels]; // 这里必须先替换成非响应式
        // console.log(panels);
        this.panels = panels.splice(i, 1);
      }
      // console.log(index);

      // block: Defines vertical alignment.
      // inline: Defines horizontal alignment.
      // this.$el.querySelector(selector)
      //   .scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });

      const scrollToAnimationFn = (number = 0, time) => {
        if (!time) {
          document.body.scrollTop = document.documentElement.scrollTop = number
          return number
        }
        const spacingTime = 20 // 设置循环的间隔时间  值越小消耗性能越高
        let spacingInex = time / spacingTime // 计算循环的次数
        let nowTop = document.body.scrollTop || document.documentElement.scrollTop // 获取当前滚动条位置
        const everTop = (number - nowTop) / spacingInex // 计算每次滑动的距离
        const scrollTimer = setInterval(() => {
          if (spacingInex > 0) {
            spacingInex--
            scrollToAnimationFn((nowTop += everTop))
          } else {
            clearInterval(scrollTimer) // 清除计时器
          }
        }, spacingTime)
      }

      let ele = this.$el.querySelector(selector);
      if (ele) {
        scrollToAnimationFn(ele.offsetTop + 45, 500)
      }
    },
    showAll() {
      this.panels = [0, 1, 2, 3, 4, 5, 6, 7]
    },
    showNone() {
      this.panels = []
    },
    sendMessage(path) {
      this.$router.push(path);
      bus.$emit('guide', 'success');
    }
  },
}
</script>
<style scoped lang="sass">
a
  text-decoration: none
  color: #2196F3

a:hover
  text-decoration: underline

.v-data-table ::v-deep th
  font-size: 0.87rem !important
.v-data-table ::v-deep td
  font-size: 0.875rem !important

.v-btn
  text-transform: none
  letter-spacing: 0em
  font-size: 0.875rem

.v-expansion-panel--active > .v-expansion-panel-header
  min-height: 45px

.v-list-item__title
  font-size: 0.875rem !important

.v-card__title
  line-height: 0.5rem

.breadcrumb
  line-height: 1
  display: inline-block
  margin: 0 0
  vertical-align: middle
  padding: 8px 15px
  margin-top: -5px
  margin-bottom: 10px
  list-style: none
  background-color: #f5f5f5
  border-radius: 4px

.v-item-group
  flex: 0 1 auto
  position: relative
  display: inline-flex
  flex-wrap: wrap
  max-width: 100%
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)

.data-table-style
  border: thin solid rgba(0, 0, 0, 0.12)

.side_bar
  position: fixed
  right: 1%
  top: 325px
  transform: translateY(-50%)
</style>